import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, AudioLines } from "lucide-react";
import { fadeUp } from "@/lib/motion";
import SpinRating from "@/components/SpinRating";
import { genreColor } from "@/lib/genreColors";

const GENRES = ["All", "Hip-Hop", "R&B", "Pop", "Rock", "Country", "Dance", "Alternative", "Global"];

export default function RhythmReviews({ reviews, archived = [] }) {
  const [tab, setTab] = useState("latest");
  const [genre, setGenre] = useState("All");
  const pool = tab === "latest" ? reviews : archived;
  const filtered =
    genre === "All"
      ? pool
      : pool.filter((r) => (r.genre || "").toLowerCase() === genre.toLowerCase());

  return (
    <section id="reviews" className="mx-auto max-w-7xl px-5 pt-16 sm:pt-24">
      <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.15 }}>
        <p className="font-rhythm-mono text-[11px] uppercase tracking-[0.22em] text-[#F3F1ED]">
          Mondays · Unfiltered
        </p>
        <div className="mt-3 flex flex-wrap items-center justify-between gap-4">
          <h2 className="font-rhythm-display text-2xl font-extrabold uppercase tracking-tight sm:text-3xl lg:text-4xl">
            {tab === "latest" ? "Latest Reviews" : "The Review Archive"}
          </h2>
          <div data-testid="rhythm-reviews-tabs" className="flex gap-2.5">
            {[
              { id: "latest", label: "Latest" },
              { id: "archive", label: `Archive (${archived.length})` },
            ].map((t) => (
              <button
                key={t.id}
                data-testid={`rhythm-reviews-tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`rounded-full border px-4 py-2 text-[11px] font-bold uppercase tracking-[0.16em] transition-colors ${
                  tab === t.id
                    ? "border-[#F3F1ED] bg-[#F3F1ED] text-[#0B0A0F]"
                    : "border-white/15 text-white/70 hover:border-[#F3F1ED] hover:text-[#F3F1ED]"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>
        <p data-testid="rhythm-rating-legend" className="mt-3 font-rhythm-mono text-[10px] uppercase tracking-[0.2em] text-white/45">
          Rated on spins — 5 spins = classic
        </p>
      </motion.div>

      <div data-testid="rhythm-genre-filter" className="mt-7 flex flex-wrap gap-2.5">
        {GENRES.map((g) => {
          const gc = genreColor(g);
          return (
            <button
              key={g}
              data-testid={`rhythm-genre-filter-${g.toLowerCase().replace(/[^a-z]/g, "")}`}
              onClick={() => setGenre(g)}
              style={genre === g || !gc ? undefined : { borderColor: gc.hex, color: gc.hex, background: gc.bg }}
              className={`rounded-full border px-4 py-2 text-[11px] font-bold uppercase tracking-[0.16em] transition-colors ${
                genre === g
                  ? "border-[#F3F1ED] bg-[#F3F1ED] text-[#0B0A0F]"
                  : "border-white/15 text-white/70 hover:border-[#F3F1ED] hover:text-[#F3F1ED]"
              }`}
            >
              {g}
            </button>
          );
        })}
      </div>

      {filtered.length === 0 ? (
        <p data-testid="rhythm-reviews-empty" className="mt-9 font-garamond text-xl italic text-white/60">
          {tab === "latest"
            ? `No ${genre === "All" ? "" : `${genre} `}reviews this week yet — the next unfiltered Monday is coming.`
            : "The archive is empty for now — every review eventually takes its seat here."}
        </p>
      ) : (
        <div className="mt-9 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((r, idx) => {
            const gc = genreColor(r.genre);
            return (
            <motion.article
              key={r.slug}
              data-testid={`rhythm-review-card-${idx}`}
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.15 }}
              style={gc ? { "--glow": gc.glow } : undefined}
              className="rhythm-card group flex flex-col overflow-hidden rounded-3xl transition-shadow duration-500 hover:shadow-[0_14px_40px_-12px_var(--glow,rgba(243,241,237,0.3))]"
            >
              <div className="overflow-hidden">
                <img
                  src={r.image_url}
                  alt={`${r.artist} — ${r.title}`}
                  className="aspect-square w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                />
              </div>
              <div className="flex flex-1 flex-col p-6">
                <div className="flex flex-wrap items-center gap-2.5">
                  <span
                    className="rounded-full border px-3 py-1 text-[10px] font-bold uppercase tracking-[0.16em]"
                    style={gc ? { borderColor: gc.hex, color: gc.hex, background: gc.bg } : undefined}
                  >
                    {r.genre}
                  </span>
                  <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-white/40">
                    {r.ptype} · {r.year}
                  </span>
                  <span className="ml-auto">
                    <SpinRating score={r.score} size={14} showValue={false} />
                  </span>
                </div>
                <h3 className="mt-4 font-rhythm-display text-lg font-bold uppercase tracking-snug">
                  {r.artist}
                </h3>
                <p className="font-garamond text-lg italic text-white/85">{r.title}</p>
                <p className="mt-3 text-sm font-bold uppercase tracking-[0.12em] text-white/60">
                  {r.review_title}
                </p>
                <p className="mt-3 line-clamp-3 text-sm leading-relaxed text-white/65">
                  {r.excerpt}
                </p>
                <p className="mt-4 font-garamond text-base italic text-white/55">
                  Reviewed by Morgan Williams
                </p>
                <Link
                  data-testid={`rhythm-review-read-${idx}`}
                  to={`/rhythm/${r.slug}`}
                  className="mt-5 inline-flex w-fit items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] text-[#F3F1ED] transition-colors hover:text-[#F3F1ED]"
                >
                  Read Review <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </div>
            </motion.article>
            );
          })}
        </div>
      )}
    </section>
  );
}
